/**
 * LayoutModeSwitcher — rozbalovací menu (jako ThemeSwitcher), nezabírá místo ve footeru.
 * LayoutModeFab — jen na úzkém desktopu (návrat na phone).
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Monitor, Smartphone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useUiLayout, type UiLayoutPref } from '../lib/ui-layout';
import { AutoLayoutIcon } from './icons/AutoLayoutIcon';

const OPTIONS: { id: UiLayoutPref; icon: typeof Monitor; label: string; tkey: string }[] = [
  { id: 'auto', icon: AutoLayoutIcon, label: 'Auto', tkey: 'layout.modeAuto' },
  { id: 'phone', icon: Smartphone, label: 'Telefon', tkey: 'layout.modePhone' },
  { id: 'desktop', icon: Monitor, label: 'Desktop', tkey: 'layout.modeDesktop' },
];

type MenuPos = { top: number; left: number; width: number; maxH: number; openUp: boolean };

/** Kompaktní trigger + popup — vedle ThemeSwitcher. */
export function LayoutModeSwitcher() {
  const { t } = useTranslation();
  const { pref, setPref, effective } = useUiLayout();
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<MenuPos | null>(null);

  const active = OPTIONS.find((o) => o.id === pref) ?? OPTIONS[0]!;
  const ActiveIcon = active.icon;

  const place = () => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const gap = 6;
    const width = Math.min(220, Math.max(r.width, 180), window.innerWidth - 16);
    const spaceBelow = window.innerHeight - r.bottom - gap;
    const spaceAbove = r.top - gap;
    const openUp = spaceBelow < 160 && spaceAbove > spaceBelow;
    const maxH = Math.min(240, Math.max(120, openUp ? spaceAbove - 8 : spaceBelow - 8));
    let left = r.right - width;
    if (left < 8) left = 8;
    if (left + width > window.innerWidth - 8) left = window.innerWidth - 8 - width;
    setPos({
      top: openUp ? r.top - gap : r.bottom + gap,
      left,
      width,
      maxH,
      openUp,
    });
  };

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const n = e.target as Node;
      if (btnRef.current?.contains(n) || menuRef.current?.contains(n)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const menu = open && pos && typeof document !== 'undefined'
    ? createPortal(
      <div
        ref={menuRef}
        role='listbox'
        aria-label={t('layout.modeTitle', 'Rozložení')}
        className='fixed z-[200] rounded-2xl border border-gray-700/80 bg-gray-950/98 p-2 shadow-2xl backdrop-blur-md'
        style={{
          top: pos.openUp ? undefined : pos.top,
          bottom: pos.openUp ? window.innerHeight - pos.top : undefined,
          left: pos.left,
          width: pos.width,
          maxHeight: pos.maxH,
        }}
      >
        <div className='px-1.5 pb-1.5 pt-0.5 text-[10px] uppercase tracking-wider text-gray-500'>
          {t('layout.modeTitle', 'Rozložení')}
        </div>
        <div className='space-y-0.5'>
          {OPTIONS.map(({ id, icon: Icon, label, tkey }) => {
            const on = pref === id;
            return (
              <button
                key={id}
                type='button'
                role='option'
                aria-selected={on}
                onClick={() => {
                  setPref(id);
                  setOpen(false);
                }}
                className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-xl text-left text-xs font-medium transition-colors ${
                  on
                    ? 'bg-plex/20 text-plex'
                    : 'text-gray-300 hover:bg-gray-800/60'
                }`}
              >
                <Icon size={14} className={on ? 'text-blue-400' : 'text-gray-500'} />
                <span className='flex-1'>{t(tkey, label)}</span>
                {on && (
                  <span className='text-[10px] text-gray-500'>
                    → {effective === 'phone'
                      ? t('layout.phone', 'Telefon')
                      : t('layout.desktop', 'Desktop')}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>,
      document.body,
    )
    : null;

  return (
    <div className='relative inline-flex'>
      <button
        ref={btnRef}
        type='button'
        onClick={() => setOpen((v) => !v)}
        title={t('layout.modeTitle', 'Rozložení')}
        aria-haspopup='listbox'
        aria-expanded={open}
        aria-label={t('layout.modeTitle', 'Rozložení')}
        className='inline-flex items-center gap-1.5 rounded-lg border border-gray-700/80 bg-gray-900/40 px-2 py-1 text-xs transition-colors hover:border-gray-500 hover:bg-gray-800/50'
      >
        <ActiveIcon size={13} className='text-gray-500 shrink-0' />
        <span className='text-gray-200 font-medium tracking-wide'>{t(active.tkey, active.label)}</span>
        <ChevronDown
          size={12}
          className={`text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {menu}
    </div>
  );
}

/** FAB jen na úzkém viewportu v desktop layoutu (návrat na phone). */
export function LayoutModeFab() {
  const { t } = useTranslation();
  const { effective, isNarrow, togglePhoneDesktop } = useUiLayout();

  if (!isNarrow && effective !== 'phone') return null;
  // Na phone shell je přepínač v hlavičce — FAB ne
  if (effective === 'phone') return null;

  return (
    <button
      type='button'
      className='layout-mode-fab'
      onClick={togglePhoneDesktop}
      title={t('layout.switchPhone', 'Přepnout na telefon')}
      aria-label={t('layout.switchPhone', 'Přepnout na telefon')}
    >
      <span className='layout-mode-fab-icon'>
        <Smartphone size={18} strokeWidth={1.75} />
      </span>
      <span className='layout-mode-fab-label'>{t('layout.phone', 'Telefon')}</span>
    </button>
  );
}
